import isNil from "lodash/isNil";
import { sortByProp } from "util/sort-by-prop";

const sortByPriorityDesc = sortByProp.bind(null, "priority", "desc");

const PRIORITY = Object.freeze({
  MAX: 99,
  DEVICE: 2,
  NETWORK: 1,
  APPLICATION: 0,
});
const LEGACY_ERROR =
  "Application parameters will not be saved, " +
  "please add to the application form below.";
const DUPLICATE_KEY_ERROR =
  "This duplicate parameter will not be saved. " +
  "Please update the value in existing one.";

function buildPriorities(defaultValue, networkParam, deviceParam) {
  const priorities = [];

  if (!isNil(defaultValue)) {
    priorities.push({
      value: defaultValue,
      priority: PRIORITY.APPLICATION,
    });
  }

  if (networkParam) {
    priorities.push({
      value: networkParam.value,
      priority: PRIORITY.NETWORK,
    });
  }

  if (deviceParam) {
    priorities.push({
      value: deviceParam.value,
      priority: PRIORITY.DEVICE,
    });
  }

  return priorities.sort(sortByPriorityDesc);
}

function getDeviceLevelParam(fieldName, deviceParams) {
  return deviceParams.find((p) => p.name === fieldName);
}

function getNetworkLevelParam(fieldName, networkParams) {
  return networkParams.find((p) => p.name === fieldName);
}

/*
 * Pick the appropriate field value from available priority values.
 *
 * When priority is not provided, pick the highest level.
 * When priority is provided, pick the NEXT available priority. This is
 * because this is used for field override determination.
 *
 * @param {string} name The field name.
 * @param {object} options Configuration options.
 * @param {any} options.defaultValue Lowest priority value.
 * @param {array} options.deviceParams Array of device priority values.
 * @param {array} options.networkParams Array of network priority values.
 *
 * @return {any} A priority value.
 * @private
 */
function getPriorityValue(name, options = {}) {
  const {
    defaultValue,
    deviceParams = [],
    networkParams = [],
    priority = PRIORITY.MAX,
  } = options;

  const deviceParam = getDeviceLevelParam(name, deviceParams);
  const networkParam = getNetworkLevelParam(name, networkParams);
  const priorities = buildPriorities(defaultValue, networkParam, deviceParam);

  const found = priorities.find((p) => p.priority < priority) || {};
  return found.value;
}

function hasDefaultValue(schema) {
  const { default_value: dv } = schema;
  return !isNil(dv);
}

function hasNetworkLevelParam(fieldName, networkParams) {
  return networkParams.some((p) => p.name === fieldName);
}

function getErrorMsg(value) {
  const errors = [];
  const { isLegacy, isDuplicate } = value;

  if (isLegacy) {
    errors.push(LEGACY_ERROR);
  }

  if (isDuplicate) {
    errors.push(DUPLICATE_KEY_ERROR);
  }

  return errors;
}

export default {
  buildPriorities,
  getDeviceLevelParam,
  getErrorMsg,
  getNetworkLevelParam,
  getPriorityValue,
  hasDefaultValue,
  hasNetworkLevelParam,
  DUPLICATE_KEY_ERROR,
  LEGACY_ERROR,
  PRIORITY,
};

export {
  buildPriorities,
  getDeviceLevelParam,
  getErrorMsg,
  getNetworkLevelParam,
  getPriorityValue,
  hasDefaultValue,
  hasNetworkLevelParam,
  DUPLICATE_KEY_ERROR,
  LEGACY_ERROR,
  PRIORITY,
};
